// How the app and Bailey talk about the app itself.
//
// Out: the person's words, then the app's context in a <beu-context> block at
// the end (after, so the conversation keeps its title from their words). The
// block is taken off again wherever a message is shown.
//
// Back: Bailey puts things on screen with fenced blocks in its reply:
//
//   ```beu
//   {"show": "post", "ask": "free slots tomorrow with Jess"}
//   ```
//
// The app reads each block, checks it strictly (only known kinds, only known
// fields, lengths capped) and draws a widget in its place. Nothing in a block
// does anything by itself: every widget acts only when the person taps it,
// and then only makes drafts or opens a page.

export const CONTEXT_OPEN = '<beu-context>'
export const CONTEXT_CLOSE = '</beu-context>'

// The person's words with the app's context after them
export const withContext = (text, context) => {
  const words = String(text || '')
  return context ? `${words}\n\n${CONTEXT_OPEN}\n${String(context).trim()}\n${CONTEXT_CLOSE}` : words
}

// What the person typed, without the app's context (also when the block was
// cut off, or the server kept only part of it)
export const stripContext = (text) => {
  const s = String(text || '')
  const at = s.indexOf(CONTEXT_OPEN)
  return at === -1 ? s : s.slice(0, at).replace(/\s+$/, '')
}

// ─── Blocks in a reply ───
const FENCE_OPEN = /^\s{0,3}(`{3,}|~{3,})\s*([A-Za-z-]*)\s*$/
const isClose = (line, fence) => new RegExp(`^\\s{0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}\\s*$`).test(line)

// A fenced block is ours when it's marked beu, or it's JSON that says what to show
const ours = (lang, body) => {
  const l = String(lang || '').toLowerCase()
  if (l === 'beu' || l === 'beu-show') return true
  return (l === 'json' || l === '') && /^\s*\{[\s\S]*"show"\s*:/.test(body)
}

// The reply as pieces in order: markdown, blocks, and (while it's still
// arriving) a block that isn't finished yet. [{ kind: 'md', text } |
// { kind: 'block', block, raw } | { kind: 'bad', raw } | { kind: 'pending' }]
export const splitReply = (text) => {
  const lines = String(text || '').split('\n')
  const out = []
  let md = []
  const flush = () => {
    const t = md.join('\n')
    if (t.trim()) out.push({ kind: 'md', text: t })
    md = []
  }
  for (let i = 0; i < lines.length; i += 1) {
    const open = FENCE_OPEN.exec(lines[i])
    if (!open) { md.push(lines[i]); continue } // eslint-disable-line no-continue
    // Find where this fence closes
    let j = i + 1
    while (j < lines.length && !isClose(lines[j], open[1])) j += 1
    const body = lines.slice(i + 1, j).join('\n')
    const closed = j < lines.length
    const lang = open[2].toLowerCase()
    // Still arriving: a beu block, or JSON that may yet say what to show
    const coming = !closed && (lang === 'beu' || ((lang === 'json' || lang === '') && /^\s*\{/.test(body)))
    if (!coming && !ours(lang, body)) {
      // Someone else's code block: leave it to the markdown
      md.push(...lines.slice(i, closed ? j + 1 : lines.length))
      i = closed ? j : lines.length
      continue // eslint-disable-line no-continue
    }
    flush()
    if (!closed) { out.push({ kind: 'pending' }); break }
    const block = readBlock(parseJson(body))
    out.push(block ? { kind: 'block', block, raw: body } : { kind: 'bad', raw: body })
    i = j
  }
  flush()
  return out
}

// A reply's words without any blocks (for previews, titles and read-aloud)
export const replyWords = (text) => splitReply(text).filter(p => p.kind === 'md').map(p => p.text.trim()).filter(Boolean).join('\n\n')

const parseJson = (body) => {
  const s = String(body || '').trim()
  if (!s) return null
  try { return JSON.parse(s) } catch (e) { /* try the outermost object */ }
  const a = s.indexOf('{')
  const b = s.lastIndexOf('}')
  if (a === -1 || b <= a) return null
  const obj = s.slice(a, b + 1)
  try { return JSON.parse(obj) } catch (e) { /* the model's trailing commas */ }
  try { return JSON.parse(obj.replace(/,(\s*[\]}])/g, '$1')) } catch (e) { return null }
}

// ─── Checking a block ───
// Text as plain words: no control characters, trimmed, capped
const clean = (v, max = 240) => {
  if (v == null) return ''
  const s = String(v).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').replace(/[ \t]+\n/g, '\n').trim() // eslint-disable-line no-control-regex
  return s.length > max ? `${s.slice(0, max - 1).trim()}…` : s
}
const oneOf = (v, list) => (list.includes(v) ? v : undefined)
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const key = (v) => (typeof v === 'string' && /^[a-zA-Z][a-zA-Z0-9]{0,40}$/.test(v) ? v : undefined)
const id = (v) => (typeof v === 'string' && /^[A-Za-z0-9_-]{6,64}$/.test(v) ? v : undefined)
const hex = (v) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(v || '').trim())
  if (!m) return undefined
  const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1]
  return `#${h.toUpperCase()}`
}
// A date and time Bailey means: 2026-10-02T18:00 (no zone: the salon's own)
const when = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(v.trim()) ? v.trim() : undefined)
const drop = (o) => Object.keys(o).reduce((a, k) => (o[k] === undefined || o[k] === '' || (Array.isArray(o[k]) && !o[k].length) ? a : { ...a, [k]: o[k] }), {})

const words = (v) => {
  if (!isObj(v)) return undefined
  const out = {}
  Object.keys(v).slice(0, 12).forEach((k) => {
    if (!key(k)) return
    const x = v[k]
    if (typeof x === 'string' || typeof x === 'number') out[k] = clean(x, 240)
    else if (Array.isArray(x)) out[k] = x.slice(0, 6).map(y => clean(y, 120)).filter(Boolean)
    else if (typeof x === 'boolean') out[k] = x
  })
  return Object.keys(out).length ? out : undefined
}
const link = (v) => {
  if (!isObj(v)) return null
  const to = key(v.to)
  if (!to) return null
  const params = isObj(v.params) ? drop({ date: when(v.params.date), staffId: id(v.params.staffId), id: id(v.params.id), tab: key(v.params.tab) }) : undefined
  return drop({ to, label: clean(v.label, 48), params: params && Object.keys(params).length ? params : undefined })
}

const post = (v) => {
  if (!isObj(v)) return null
  const p = drop({
    ask: clean(v.ask, 300),
    kind: key(v.kind),
    words: words(v.words),
    format: oneOf(v.format, ['post', 'story']),
    when: when(v.when),
    style: key(v.style),
    layout: key(v.layout),
    colourway: key(v.colourway),
    title: clean(v.title, 80),
  })
  return p.ask || p.kind ? p : null
}

const READERS = {
  // One social post, made by the app from what's asked and the salon's own facts
  post,
  // A few posts at once (a week of posts)
  posts: (v) => {
    const items = (Array.isArray(v.items) ? v.items : []).slice(0, 7).map(post).filter(Boolean)
    return items.length ? { items, title: clean(v.title, 80) || undefined } : null
  },
  // A marketing text, ready to save as a draft
  text: (v) => {
    const t = drop({ message: clean(v.message, 900), title: clean(v.title, 80), draftId: id(v.draftId) })
    return t.message || t.draftId ? t : null
  },
  // An email, drawn in the salon's brand
  email: (v) => {
    const body = Array.isArray(v.body) ? v.body.slice(0, 6).map(p => clean(p, 700)).filter(Boolean) : clean(v.body, 2400).split(/\n{2,}/).filter(Boolean).slice(0, 6)
    const e = drop({
      subject: clean(v.subject, 120),
      preheader: clean(v.preheader, 160),
      heading: clean(v.heading, 120),
      body,
      button: clean(v.button, 40),
      audience: clean(v.audience, 60),
      // Which of the salon's templates it wears: filling quiet days, a
      // moment in the year, winning people back, or introducing someone
      purpose: oneOf(v.purpose, ['fill', 'moment', 'winback', 'intro']),
      // Or one of their templates by id (barber-refer, all-price-update)
      template: typeof v.template === 'string' && /^[a-zA-Z][a-zA-Z0-9-]{1,40}$/.test(v.template) ? v.template : undefined,
      draftId: id(v.draftId),
      title: clean(v.title, 80),
    })
    return e.subject || e.heading || (e.body && e.body.length) || e.draftId ? e : null
  },
  // Brand colours, fonts and tone to look at, and save when they're right
  brand: (v) => {
    const colours = (Array.isArray(v.colours) ? v.colours : Array.isArray(v.colors) ? v.colors : []).map(hex).filter(Boolean).slice(0, 6)
    const fonts = isObj(v.fonts) ? drop({ display: clean(v.fonts.display, 40), text: clean(v.fonts.text, 40) }) : undefined
    const b = drop({ colours, fonts: fonts && Object.keys(fonts).length ? fonts : undefined, tone: clean(v.tone, 240), title: clean(v.title, 80) })
    return b.colours || b.fonts || b.tone ? b : null
  },
  // Places in Beu, one tap away
  links: (v) => {
    const items = (Array.isArray(v.items) ? v.items : []).slice(0, 5).map(link).filter(Boolean)
    return items.length ? { items, title: clean(v.title, 80) || undefined } : null
  },
  // How to do something, step by step, each step with where it's done
  guide: (v) => {
    const steps = (Array.isArray(v.steps) ? v.steps : []).slice(0, 8).map((s) => {
      if (typeof s === 'string') return { text: clean(s, 220) }
      if (!isObj(s)) return null
      const l = s.to ? link({ to: s.to, label: s.label, params: s.params }) : null
      return drop({ text: clean(s.text, 220), ...(l || {}) })
    }).filter(s => s && s.text)
    return steps.length ? { title: clean(v.title, 80) || undefined, steps } : null
  },
  // Bailey's ready-made plans (the same as Marketing home), optionally one
  // kind: fill (quiet days), season, review, team, winback
  plans: (v) => {
    // One plan changed in place: "plan" is its key from PLANS ON SCREEN, and
    // each edit names an item there (sms, email, story2, post...)
    const planKey = typeof v.plan === 'string' && /^[a-z]+:[A-Za-z0-9:_-]{1,60}$/.test(v.plan.trim()) ? v.plan.trim() : undefined
    const edits = planKey && Array.isArray(v.edits) ? v.edits.slice(0, 12).map((e) => {
      if (!isObj(e) || typeof e.item !== 'string' || !/^[a-z]+[0-9]{0,2}$/.test(e.item)) return null
      const x = drop({
        item: e.item,
        on: typeof e.on === 'boolean' ? e.on : undefined,
        at: when(e.at),
        message: clean(e.message, 600),
        subject: clean(e.subject, 120),
        heading: clean(e.heading, 120),
        body: clean(e.body, 1200),
        headline: clean(e.headline, 120),
        cta: clean(e.cta, 80),
      })
      return Object.keys(x).length > 1 ? x : null
    }).filter(Boolean) : undefined
    return {
      ...drop({ title: clean(v.title, 80), focus: oneOf(v.focus, ['fill', 'season', 'review', 'team', 'winback']), max: Number.isInteger(v.max) && v.max >= 1 && v.max <= 3 ? v.max : undefined, plan: planKey, edits }),
      all: true,
    }
  },
  // Bailey's Playbook: one option of the server's pick (or an alternative),
  // built as a plan on the same card as "plans". "key" is the scenario's key
  // from the PLAYBOOK in Bailey's instructions, "option" A, B or C.
  playbook: (v) => {
    const k = typeof v.key === 'string' && /^[A-Za-z0-9:_-]{1,120}$/.test(v.key.trim()) ? v.key.trim() : undefined
    const option = oneOf(typeof v.option === 'string' ? v.option.trim().toUpperCase() : '', ['A', 'B', 'C'])
    return k ? drop({ key: k, option, title: clean(v.title, 80) }) : null
  },
  // A question with two or three answers to tap; each sends its words back as
  // theirs. The first is what Bailey suggests. A tap sends exactly the words
  // on the button: a separate "say" could put different words in their mouth
  // (a harmless label sending an instruction), so it's ignored.
  choices: (v) => {
    const items = (Array.isArray(v.items) ? v.items : []).slice(0, 6).map((c) => {
      const label = typeof c === 'string' ? clean(c, 48) : (isObj(c) ? clean(c.label, 48) : '')
      return label ? { label, say: label } : null
    }).filter(Boolean).slice(0, 3)
    return items.length >= 2 ? { items } : null
  },
}
export const SHOWS = Object.keys(READERS)

// A block as the app will use it, or null when it isn't one it knows
export const readBlock = (obj) => {
  if (!isObj(obj)) return null
  const show = typeof obj.show === 'string' ? obj.show.trim().toLowerCase() : ''
  const read = READERS[show]
  if (!read) return null
  const block = read(obj)
  return block ? { show, ...block } : null
}

// What a reply put on screen, in a line for the desk pet's bubble ("A post is
// ready to look at"), or '' when it's only words
const NOUNS = {
  post: ['A post is', 'posts are'],
  posts: ['Posts are', 'sets of posts are'],
  email: ['An email is', 'emails are'],
  brand: ['Your brand is', 'brand ideas are'],
  links: ['Somewhere to go is', 'places to go are'],
  guide: ['The steps are', 'guides are'],
  choices: ['A question is', 'questions are'],
  plans: ['Bailey’s plans are', 'plans are'],
  playbook: ['A plan is', 'plans are'],
}
export const readyLine = (text) => {
  const all = splitReply(text).filter(p => p.kind === 'block').map(p => p.block)
  // A question beside something to look at doesn't count as a thing
  const blocks = all.some(b => b.show !== 'choices') ? all.filter(b => b.show !== 'choices') : all
  if (!blocks.length) return ''
  if (blocks.length === 1) {
    const b = blocks[0]
    if (b.show === 'posts') return `${b.items.length} posts are ready to look at`
    if (b.show === 'choices') return 'Bailey has a question for you'
    return `${NOUNS[b.show][0]} ready to look at`
  }
  return `${blocks.length} things are ready to look at`
}
